/**
 * PublicRecordDetailContainer — "official register" layout for record detail
 * pages. The alternative to PublicRecordDetail's boxed card.
 *
 * Presents the record like an entry in a public register:
 *   • Letterhead above a double rule: page title as the register name, the
 *     mapped title and subtitle on the left; reference no. + status badge right.
 *   • Cover image as a captioned plate (click to zoom), or a themed tint band
 *     when no image is mapped.
 *   • Mapped description as the opening statement.
 *   • Date, cost, then every remaining field as ruled label-left rows.
 *
 * Typography and colour follow the app's existing tokens — the base/mono font
 * families from App.css, the theme accent, and the slate palette the other
 * public components use. detailColumns sets the density of the ruled rows:
 * 1 = one wide column (large variant), 2 / 3 = flowed columns.
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../theme/ThemeContext';
import PublicFieldValue from './PublicFieldValue';
import PublicTagBadges from './PublicTagBadges';
import envConfig from '../../config/envConfig';
import { getCurrencyDisplay } from '../../utils/currencies';
import {
  formatInstantTime, localeDateIn, parseInstant, toDisplayString, zoneOf,
} from '../../utils/utils';

const FONT_BASE = 'var(--font-family-base)';
const FONT_MONO = 'var(--font-family-mono)';

// Shared slate palette — the same values the card layout and public grids use.
const INK = '#1e293b';
const INK_SOFT = '#334155';
const MUTED = '#64748b';
const LABEL = '#94a3b8';
const EMPTY = '#cbd5e1';
const RULE = '#e8edf3';

function resolveFileUrl(value) {
  if (!value) return null;
  if (value.startsWith('http://') || value.startsWith('https://')) return value;
  return `${envConfig.fileServerUrl}/public/${value}`;
}

function isBlank(value) {
  return value === null || value === undefined || value === '';
}

// A bare calendar date: "2026-07-28", no time component.
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Formats the mapped date for the dateline. A value that carries a time
 * ("2026-07-28T14:30:00Z", or a datetime-typed field) renders as date + time;
 * a plain calendar date renders as the date alone rather than an invented
 * "12:00 AM".
 *
 * Date-only values are split into components and rebuilt as a local date —
 * `new Date('2026-07-28')` is parsed as UTC midnight, which renders as the
 * previous day for any viewer behind UTC.
 */
function formatDateline(value, fieldType, timeZone) {
  if (isBlank(value)) return null;

  const raw = String(value).trim();
  const dateOnly = fieldType === 'date' || DATE_ONLY_RE.test(raw);
  const parsed = parseInstant(raw);
  if (!parsed) return toDisplayString(value);

  // A day is a day everywhere. Reading it in the record's zone would shift it
  // across midnight for no gain, so only real instants are zoned.
  if (dateOnly) {
    return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  }

  // Date and time are formatted separately and joined with a plain space:
  // toLocaleString would splice in a locale connector ("July 28, 2026 at 7:30 AM").
  const datePart = localeDateIn(
    parsed, { year: 'numeric', month: 'long', day: 'numeric' }, timeZone,
  );
  return `${datePart} ${formatInstantTime(parsed, timeZone)}`;
}

/**
 * Size scale. At 1 column there is a single wide reading column and nothing
 * competing with it, so the whole layout steps up — larger type, taller plate,
 * roomier rows. 2 / 3 columns stay compact so the flowed rows keep density.
 */
function getScale(cols) {
  const shared = {
    // Plate / letterhead / body padding, ref no. and eyebrow sizes are
    // identical at every column count — only the reading sizes below change.
    // The plate's bottom gap comes from headPadding's top, so it stays 0 here.
    platePadding: '28px clamp(24px, 4vw, 44px) 0',
    headPadding: '30px clamp(24px, 4vw, 44px) 24px',
    bodyPadding: '28px clamp(24px, 4vw, 44px) 40px',
    eyebrowFont: '0.68rem',
    refFont: '1.3rem',
    rowLabelFont: '0.72rem',
    columnCount: cols,
  };

  if (cols === 1) {
    // Large variant: one wide reading column, so type and plate step up.
    return {
      ...shared,
      large: true,
      titleFont: 'clamp(2.1rem, 3.4vw, 2.8rem)',
      subtitleFont: '1.2rem',
      datelineFont: '1rem',
      plateHeight: 680,
      descriptionFont: '1.2rem',
      descriptionLine: 1.8,
      rowPadding: '15px 0',
      rowValueFont: '1.1rem',
      rowGridCols: '210px 1fr',
    };
  }

  return {
    ...shared,
    large: false,
    titleFont: 'clamp(1.9rem, 3.4vw, 2.4rem)',
    subtitleFont: '1.1rem',
    datelineFont: '0.94rem',
    plateHeight: 560,
    descriptionFont: '1.16rem',
    descriptionLine: 1.8,
    rowPadding: '13px 0',
    rowValueFont: '1rem',
    // At 3 columns a fixed label gutter leaves no room for the value, so those
    // rows stack the label above it instead.
    rowGridCols: cols === 3 ? null : '150px 1fr',
  };
}

export default function PublicRecordDetailContainer({
  record = {},
  fields = [],
  mapping = {},
  branding = {},
  detailColumns = 1,
}) {
  const [lightbox, setLightbox] = useState(false);
  const { theme } = useTheme();
  const accentColor = (branding.accent_color && !branding.accent_color.startsWith('var('))
    ? branding.accent_color
    : theme.primary;
  // Match the public card layout: .pc-card in PublicCardGrid is a fixed 16px
  // and its chips are full pills, so the detail page uses the same values
  // rather than the theme radius — the two pages sit side by side in one flow.
  const cardRadius = 16;
  const badgeRadius = 999;
  const cols = [1, 2, 3].includes(Number(detailColumns)) ? Number(detailColumns) : 1;
  const scale = getScale(cols);

  useEffect(() => {
    if (!lightbox) return;
    const handler = (e) => { if (e.key === 'Escape') setLightbox(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightbox]);

  const fieldMap = Object.fromEntries(fields.map(f => [f.name, f]));
  const badgeColors = mapping.badge_colors || {};

  // ── Cover image ───────────────────────────────────────────────────────────
  const imageMeta = mapping.image_field ? fieldMap[mapping.image_field] : null;
  const isRealImageField = imageMeta?.data_type === 'image';
  const imageUrl = isRealImageField ? resolveFileUrl(record[mapping.image_field]) : null;
  const hasImage = !!imageUrl;

  // ── Mapped values ─────────────────────────────────────────────────────────
  const titleVal = mapping.title_field ? record[mapping.title_field] : null;
  const subtitleVal = mapping.subtitle_field ? record[mapping.subtitle_field] : null;
  const badgeVal = mapping.badge_field ? record[mapping.badge_field] : null;
  const dateVal = mapping.date_field ? record[mapping.date_field] : null;
  const costVal = mapping.cost_field ? record[mapping.cost_field] : null;
  const descriptionVal = mapping.description_field ? record[mapping.description_field] : null;
  const tagsVal = mapping.tags_field ? record[mapping.tags_field] : null;

  const dateDisplay = formatDateline(
    dateVal, fieldMap[mapping.date_field]?.data_type, zoneOf(record),
  );

  // getCurrencyDisplay returns either a bare symbol ("$") or "symbol CODE" for
  // symbols shared across currencies — a space keeps both readable.
  const costDisplay = isBlank(costVal)
    ? null
    : `${getCurrencyDisplay(mapping.cost_currency) || ''} ${toDisplayString(costVal)}`.trim();

  // Every mapped field is rendered in its own dedicated slot, so none of them
  // may also appear in the ruled rows below:
  //   image / title / subtitle / badge → letterhead
  //   date                             → dateline under the title
  //   description                      → opening statement
  //   cost                             → leading ruled row, currency-formatted
  const promotedNames = new Set(
    [
      isRealImageField ? mapping.image_field : null,
      !isBlank(titleVal) ? mapping.title_field : null,
      !isBlank(subtitleVal) ? mapping.subtitle_field : null,
      !isBlank(badgeVal) ? mapping.badge_field : null,
      !isBlank(descriptionVal) ? mapping.description_field : null,
      !isBlank(tagsVal) ? mapping.tags_field : null,
      dateDisplay ? mapping.date_field : null,
      costDisplay ? mapping.cost_field : null,
    ].filter(Boolean)
  );

  const gridFields = fields.filter(f => !promotedNames.has(f.name));

  // Ordered ruled rows: mapped cost, then the remaining fields. The date is not
  // here — it reads as a dateline beside the title instead.
  const rows = [];
  if (costDisplay) {
    rows.push({
      key: '__cost',
      label: fieldMap[mapping.cost_field]?.display_name || 'Value',
      node: <span style={{ color: INK, fontWeight: 600 }}>{costDisplay}</span>,
    });
  }
  gridFields.forEach(field => {
    const val = record[field.name];
    const empty = isBlank(val);
    const badgeColor = (!empty && field.data_type === 'enum') ? (badgeColors[val] || 'primary') : undefined;
    const isJson = field.data_type === 'json' || field.data_type === 'jsonb';
    const isMono = ['number', 'integer', 'float', 'decimal', 'numeric', 'uuid'].includes(field.data_type);
    rows.push({
      key: field.name,
      label: field.display_name || field.name,
      node: empty
        ? <span style={{ color: EMPTY }}>—</span>
        : (
          <span style={{
            color: INK,
            fontFamily: isMono ? FONT_MONO : undefined,
            ...(isJson
              ? { display: 'block', maxHeight: '14rem', overflow: 'auto', whiteSpace: 'pre', fontFamily: FONT_MONO, fontSize: '0.85rem' }
              : {}),
          }}>
            <PublicFieldValue field={field} value={val} badgeColor={badgeColor} timeZone={zoneOf(record)} />
          </span>
        ),
    });
  });

  const referenceVal = mapping.reference_field
    ? record[mapping.reference_field]
    : (record.reference ?? record.id);

  const Row = ({ label, node }) => (
    <div style={{
      padding: scale.rowPadding,
      borderTop: `1px solid ${RULE}`,
      ...(scale.rowGridCols
        ? { display: 'grid', gridTemplateColumns: scale.rowGridCols, gap: 16, alignItems: 'baseline' }
        : {}),
    }}>
      <div style={{
        fontFamily: FONT_MONO,
        fontSize: scale.rowLabelFont,
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: LABEL,
        lineHeight: 1.5,
        ...(scale.rowGridCols ? {} : { marginBottom: 5 }),
      }}>
        {label}
      </div>
      <div style={{
        fontSize: scale.rowValueFont,
        lineHeight: 1.6,
        wordBreak: 'break-word',
        minWidth: 0,
      }}>
        {node}
      </div>
    </div>
  );

  /* Cover plate — sits above the letterhead so the image is the first thing on
     the page. Everything else about it (framing, zoom, tinted fallback) is
     unchanged from when it lived in the body. */
  const coverPlate = hasImage ? (
    /* Click (or Enter/Space) opens the full-size preview */
    <figure style={{ margin: 0 }}>
      <div
        role="button"
        tabIndex={0}
        aria-label="Open full-size image"
        onClick={() => setLightbox(true)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setLightbox(true);
          }
        }}
        onMouseEnter={e => { e.currentTarget.querySelector('.plate-zoom').style.opacity = '1'; }}
        onMouseLeave={e => { e.currentTarget.querySelector('.plate-zoom').style.opacity = '0'; }}
        style={{
          position: 'relative',
          display: 'block',
          border: `1px solid ${RULE}`,
          borderRadius: cardRadius,
          overflow: 'hidden',
          cursor: 'zoom-in',
          boxShadow: '0 10px 28px rgba(15,23,42,0.10), 0 2px 6px rgba(15,23,42,0.05)',
        }}
      >
        <img
          src={imageUrl}
          alt={titleVal || 'plate'}
          onError={e => { e.target.closest('figure').style.display = 'none'; }}
          style={{
            width: '100%',
            maxHeight: scale.plateHeight,
            objectFit: 'contain',
            background: '#fcfdfe',
            display: 'block',
          }}
        />
        {/* Zoom affordance — fades in on hover so the image reads as clickable */}
        <span
          className="plate-zoom"
          style={{
            position: 'absolute', top: 12, right: 12,
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '6px 12px', borderRadius: 999,
            background: 'rgba(15,23,42,0.72)', color: '#fff',
            fontSize: '0.72rem', fontWeight: 600,
            opacity: 0, transition: 'opacity 160ms ease',
            pointerEvents: 'none',
          }}
        >
          <i className="bi bi-arrows-fullscreen" style={{ fontSize: '0.7rem' }} />
          Click to preview
        </span>
      </div>
    </figure>
  ) : (
    /* No image mapped (or the record has none) — a tinted plate with the
       app mark, so the slot reads as intentional rather than broken. */
    <div style={{
      position: 'relative',
      height: scale.large ? 300 : 240,
      borderRadius: cardRadius,
      border: `1px solid ${RULE}`,
      background: `linear-gradient(135deg, ${accentColor}07, ${accentColor}12)`,
      boxShadow: '0 10px 28px rgba(15,23,42,0.08), 0 2px 6px rgba(15,23,42,0.04)',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 14,
    }}>
      {/* Dot texture, matching the card layout's placeholder */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute', inset: 0,
          backgroundImage: `radial-gradient(circle, ${accentColor}1c 1px, transparent 1px)`,
          backgroundSize: '22px 22px',
          opacity: 0.45,
        }}
      />
      <img
        src="/favicon.svg"
        alt=""
        aria-hidden="true"
        style={{
          position: 'relative',
          width: scale.large ? 96 : 76,
          height: scale.large ? 96 : 76,
          opacity: 0.2,
          userSelect: 'none',
        }}
        onError={e => { e.target.style.display = 'none'; }}
      />
      <span style={{
        position: 'relative',
        fontFamily: FONT_MONO,
        fontSize: '0.68rem',
        fontWeight: 600,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: MUTED,
      }}>
        No image on record
      </span>
    </div>
  );

  return (
    <div style={{
      background: '#fff',
      border: `1px solid ${RULE}`,
      borderRadius: cardRadius,
      overflow: 'hidden',
      color: INK,
      fontFamily: FONT_BASE,
      boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
    }}>

      {/* ── Cover plate ────────────────────────────────────────────────────── */}
      <div style={{ padding: scale.platePadding }}>
        {coverPlate}
      </div>

      {/* ── Letterhead ─────────────────────────────────────────────────────── */}
      <div style={{
        borderBottom: `3px double ${accentColor}59`,
        padding: scale.headPadding,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 24,
        flexWrap: 'wrap',
      }}>
        <div style={{ minWidth: 0, flex: '1 1 320px' }}>
          {branding.page_title && (
            <div style={{
              fontFamily: FONT_MONO,
              fontSize: scale.eyebrowFont,
              fontWeight: 600,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: accentColor,
              marginBottom: 10,
            }}>
              {branding.page_title}
            </div>
          )}
          {!isBlank(titleVal) && (
            <h1 style={{
              margin: 0,
              fontSize: scale.titleFont,
              fontWeight: 600,
              lineHeight: 1.15,
              letterSpacing: '-0.02em',
              color: INK,
            }}>
              {toDisplayString(titleVal)}
            </h1>
          )}
          {/* Dateline — sits directly under the title, ahead of the subtitle:
              it dates the entry rather than being another attribute of it, so
              it carries no label. */}
          {dateDisplay && (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              marginTop: 10,
              fontSize: scale.datelineFont,
              fontWeight: 500,
              color: MUTED,
            }}>
              <i
                className="bi bi-calendar3"
                aria-hidden="true"
                style={{ fontSize: '0.85em', color: accentColor }}
              />
              <time dateTime={isBlank(dateVal) ? undefined : String(dateVal)}>
                {dateDisplay}
              </time>
            </div>
          )}

          {!isBlank(subtitleVal) && (
            <div style={{
              fontSize: scale.subtitleFont,
              fontWeight: 400,
              color: MUTED,
              marginTop: 8,
            }}>
              {toDisplayString(subtitleVal)}
            </div>
          )}
        </div>

        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          {!isBlank(referenceVal) && (
            <>
              <div style={{
                fontFamily: FONT_MONO,
                fontSize: '0.62rem',
                fontWeight: 700,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: LABEL,
                marginBottom: 6,
              }}>
                Ref. no.
              </div>
              <div style={{ fontFamily: FONT_MONO, fontSize: scale.refFont, color: INK }}>
                {toDisplayString(referenceVal)}
              </div>
            </>
          )}
          {!isBlank(badgeVal) && (
            <div style={{
              marginTop: 14,
              display: 'inline-flex',
              alignItems: 'center',
              border: `1.5px solid ${accentColor}59`,
              background: `${accentColor}0f`,
              padding: '6px 12px',
              borderRadius: badgeRadius,
            }}>
              <span style={{
                fontFamily: FONT_MONO,
                fontSize: '0.66rem',
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: accentColor,
              }}>
                {toDisplayString(badgeVal)}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* ── Body ───────────────────────────────────────────────────────────── */}
      <div style={{ padding: scale.bodyPadding }}>

        {/* Opening statement */}
        {!isBlank(descriptionVal) && (
          <p style={{
            margin: isBlank(tagsVal) ? '0 0 1.9rem' : '0 0 0.9rem',
            fontSize: scale.descriptionFont,
            lineHeight: scale.descriptionLine,
            color: INK_SOFT,
            whiteSpace: 'pre-wrap',
            maxWidth: '68ch',
          }}>
            {toDisplayString(descriptionVal)}
          </p>
        )}

        {/* Tags — sit directly below the opening statement */}
        {!isBlank(tagsVal) && (
          <div style={{ marginBottom: '1.9rem' }}>
            <PublicTagBadges value={tagsVal} bgColor={mapping.tag_bg_color} accentColor={accentColor} />
          </div>
        )}

        {/* Ruled register rows */}
        {rows.length > 0 ? (
          <div style={{
            borderBottom: `1px solid ${RULE}`,
            ...(scale.columnCount > 1 ? { columnCount: scale.columnCount, columnGap: '48px' } : {}),
          }}>
            {rows.map(r => (
              <div key={r.key} style={scale.columnCount > 1 ? { breakInside: 'avoid' } : undefined}>
                <Row label={r.label} node={r.node} />
              </div>
            ))}
          </div>
        ) : (
          isBlank(descriptionVal) && isBlank(tagsVal) && (
            <p style={{ color: LABEL, fontSize: '0.9rem', margin: 0 }}>No fields to display.</p>
          )
        )}
      </div>

      {/* ── Lightbox ───────────────────────────────────────────────────────── */}
      {lightbox && hasImage && createPortal(
        <div
          onClick={() => setLightbox(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 99998,
            background: 'rgba(0,0,0,0.88)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'zoom-out',
          }}
        >
          <img
            src={imageUrl}
            alt={titleVal || 'plate'}
            onClick={e => e.stopPropagation()}
            style={{
              maxWidth: '92vw', maxHeight: '90vh', objectFit: 'contain',
              borderRadius: 8, boxShadow: '0 8px 48px rgba(0,0,0,0.6)', cursor: 'default',
            }}
          />
          <button
            onClick={() => setLightbox(false)}
            style={{
              position: 'absolute', top: 16, right: 20,
              background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff',
              fontSize: '1.4rem', lineHeight: 1, width: 38, height: 38, borderRadius: '50%',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>,
        document.body
      )}
    </div>
  );
}
